/**
 * Mobile {@link FileStore}, backed by the native shell over the Capacitor
 * bridge.
 *
 * Identical in shape to the Electron adapter, and for the same reason: one
 * `App` component serves the browser, the desktop and the phone because all
 * three satisfy this port. What differs is only what the other side can do.
 *
 * Bytes cross the bridge base64-encoded rather than as a typed array. That is
 * not a preference — Capacitor serialises plugin arguments through JSON, and a
 * `Uint8Array` would arrive as an object with numeric keys. Encoding
 * explicitly is slower than the desktop's structured clone and is the reason
 * §11's range reads matter more here than anywhere else: on a phone the app
 * should transfer the first page, not the file.
 */
import { FileStoreError } from '@moduviewer/core';
import type {
  FileStat, FileStore, OriginalRef, RecoveryPoint, SaveOutcome, WorkingCopyRef,
} from '@moduviewer/core';

export interface MobileIncomingFile {
  readonly handle: string;
  readonly displayName: string;
  readonly sizeBytes: number;
  readonly modifiedAtMs: number;
  readonly source: OriginalRef['source'];
  /** What the sender claimed. A hint for detection, never the answer (§4). */
  readonly declaredMime?: string;
}

/** The plugin surface the native shells expose. */
interface ModuFilesPlugin {
  ready(): Promise<void>;
  stat(options: { handle: string }): Promise<FileStat>;
  readAll(options: { handle: string }): Promise<{ data: string }>;
  readRange(options: { handle: string; start: number; end: number }): Promise<{ data: string }>;
  createWorkingCopy(options: { original: OriginalRef; sessionId: string }): Promise<WorkingCopyRef>;
  writeWorkingCopy(options: { ref: WorkingCopyRef; data: string }): Promise<void>;
  readWorkingCopy(options: { ref: WorkingCopyRef }): Promise<{ data: string }>;
  releaseWorkingCopy(options: { ref: WorkingCopyRef }): Promise<void>;
  writeRecoveryPoint(options: {
    sessionId: string; revision: number; data: string; atMs: string;
  }): Promise<RecoveryPoint>;
  listRecoveryPoints(options: { sessionId?: string }): Promise<{ points: RecoveryPoint[] }>;
  readRecoveryPoint(options: { point: RecoveryPoint }): Promise<{ data: string }>;
  deleteRecoveryPoints(options: { points: readonly RecoveryPoint[] }): Promise<void>;
  sweepCache(options: { olderThanMs: string; nowMs: string }): Promise<{ removed: number }>;
  stageBytes(options: { data: string; name: string }): Promise<{ handle: string }>;
  exists(options: { name: string }): Promise<{ exists: boolean }>;
  saveAs(options: { data: string; suggestedName: string; revision: number }): Promise<SaveOutcome>;
  overwriteOriginal(options: {
    original: OriginalRef; data: string; revision: number;
  }): Promise<SaveOutcome>;
}

interface CapacitorGlobal {
  readonly Plugins?: { readonly ModuFiles?: ModuFilesPlugin };
  getPlatform?(): string;
  isNativePlatform?(): boolean;
}

function capacitor(): CapacitorGlobal | null {
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor ?? null;
}

function plugin(): ModuFilesPlugin | null {
  return capacitor()?.Plugins?.ModuFiles ?? null;
}

/** True when running inside the Android or iOS shell. */
export function isMobileShell(): boolean {
  const cap = capacitor();
  return cap?.isNativePlatform?.() === true && plugin() !== null;
}

/** `'android'`, `'ios'`, or null in the browser. */
export function mobilePlatform(): 'android' | 'ios' | null {
  const name = capacitor()?.getPlatform?.();
  return name === 'android' || name === 'ios' ? name : null;
}

/* ------------------------------------------------------------------ *
 * base64
 * ------------------------------------------------------------------ */

/**
 * Encode in chunks.
 *
 * `String.fromCharCode(...bytes)` on a multi-megabyte array overflows the
 * argument limit and throws — a bug that only shows up on real documents,
 * which is exactly the kind this project has to not ship.
 */
function toBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function fromBase64(encoded: string): Uint8Array {
  const binary = atob(encoded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

export const base64 = { encode: toBase64, decode: fromBase64 };

/* ------------------------------------------------------------------ *
 * the store
 * ------------------------------------------------------------------ */

export class CapacitorFileStore implements FileStore {
  readonly #api: ModuFilesPlugin;

  constructor() {
    const api = plugin();
    if (!api) throw new Error('the mobile file plugin is not available');
    this.#api = api;
  }

  /**
   * Tell the shell that listeners are attached.
   *
   * On a cold start the native side handles the launch intent long before any
   * JavaScript exists, so it holds the event until this is called. Without it
   * the very first file — the one the user tapped — is the one that fails to
   * open.
   */
  async ready(): Promise<void> {
    await this.#api.ready();
  }

  async stat(handle: string): Promise<FileStat> {
    return this.#api.stat({ handle });
  }

  async readRange(handle: string, start: number, end: number): Promise<Uint8Array> {
    return wrap(async () => fromBase64((await this.#api.readRange({ handle, start, end })).data));
  }

  async readAll(handle: string): Promise<Uint8Array> {
    return wrap(async () => fromBase64((await this.#api.readAll({ handle })).data));
  }

  async createWorkingCopy(original: OriginalRef, sessionId: string): Promise<WorkingCopyRef> {
    return wrap(() => this.#api.createWorkingCopy({ original, sessionId }));
  }

  async writeWorkingCopy(ref: WorkingCopyRef, bytes: Uint8Array): Promise<void> {
    return wrap(() => this.#api.writeWorkingCopy({ ref, data: toBase64(bytes) }));
  }

  async readWorkingCopy(ref: WorkingCopyRef): Promise<Uint8Array> {
    return wrap(async () => fromBase64((await this.#api.readWorkingCopy({ ref })).data));
  }

  async releaseWorkingCopy(ref: WorkingCopyRef): Promise<void> {
    return wrap(() => this.#api.releaseWorkingCopy({ ref }));
  }

  async writeRecoveryPoint(
    sessionId: string, revision: number, bytes: Uint8Array, atMs: number,
  ): Promise<RecoveryPoint> {
    // Timestamps go across as strings: the bridge marshals numbers through
    // 32-bit integers on Android, and a millisecond epoch does not fit.
    return wrap(() => this.#api.writeRecoveryPoint({
      sessionId, revision, data: toBase64(bytes), atMs: String(atMs),
    }));
  }

  async listRecoveryPoints(sessionId?: string): Promise<RecoveryPoint[]> {
    return wrap(async () => (await this.#api.listRecoveryPoints(
      sessionId === undefined ? {} : { sessionId },
    )).points);
  }

  async readRecoveryPoint(point: RecoveryPoint): Promise<Uint8Array> {
    return wrap(async () => fromBase64((await this.#api.readRecoveryPoint({ point })).data));
  }

  async deleteRecoveryPoints(points: readonly RecoveryPoint[]): Promise<void> {
    return wrap(() => this.#api.deleteRecoveryPoints({ points }));
  }

  async exists(name: string): Promise<boolean> {
    return (await this.#api.exists({ name })).exists;
  }

  async saveAs(bytes: Uint8Array, suggestedName: string, revision: number): Promise<SaveOutcome> {
    return this.#api.saveAs({ data: toBase64(bytes), suggestedName, revision });
  }

  async overwriteOriginal(
    original: OriginalRef, bytes: Uint8Array, revision: number,
  ): Promise<SaveOutcome> {
    // Both shells answer `unsupported` here: the app holds a copy of the file,
    // not the file, so replacing the original is a claim it cannot honour
    // (§19). The session falls back to Save As, which is §13's default anyway.
    return this.#api.overwriteOriginal({ original, data: toBase64(bytes), revision });
  }

  async sweepCache(olderThanMs: number, nowMs: number): Promise<number> {
    return (await this.#api.sweepCache({
      olderThanMs: String(olderThanMs), nowMs: String(nowMs),
    })).removed;
  }

  /** Stage app-produced bytes — an entry taken out of an archive (§21). */
  async stageBytes(bytes: Uint8Array, name: string): Promise<string> {
    return wrap(async () => (await this.#api.stageBytes({ data: toBase64(bytes), name })).handle);
  }
}

/**
 * A plugin rejection arrives as a string. Re-typing it here keeps the
 * controller's failure classification — and therefore §34's messages —
 * working across the bridge.
 */
async function wrap<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (err) {
    const message = String((err as Error)?.message ?? err);
    const reason = /ENOSPC|no space|storage/i.test(message) ? 'storage-full'
      : /permission|EACCES|EPERM|scope/i.test(message) ? 'permission-denied'
        : 'io-error';
    throw new FileStoreError(reason, message);
  }
}

/* ------------------------------------------------------------------ *
 * events
 * ------------------------------------------------------------------ */

/**
 * Files opened from another app, the share sheet or the Files app.
 *
 * Delivered as window events rather than plugin listeners so the same
 * subscription shape works on both platforms and needs no plugin instance.
 */
export function onMobileIncomingFile(
  listener: (file: MobileIncomingFile) => void,
): () => void {
  const handler = (event: Event): void => {
    const detail = (event as CustomEvent<unknown>).detail;
    const parsed = typeof detail === 'string' ? JSON.parse(detail) : detail;
    listener(parsed as MobileIncomingFile);
  };
  window.addEventListener('moduOpenFile', handler);
  return () => window.removeEventListener('moduOpenFile', handler);
}

export function onMobileOpenFailed(
  listener: (info: { path: string; messageKey: string }) => void,
): () => void {
  const handler = (event: Event): void => {
    const detail = (event as CustomEvent<unknown>).detail;
    const parsed = typeof detail === 'string' ? JSON.parse(detail) : detail;
    listener(parsed as { path: string; messageKey: string });
  };
  window.addEventListener('moduOpenFailed', handler);
  return () => window.removeEventListener('moduOpenFailed', handler);
}

/**
 * The shell warning that the app is about to be backgrounded (§23).
 *
 * A backgrounded mobile app may never run again — the system reclaims it
 * without notice. The autosave timer is not enough on its own; this is the
 * last moment at which an edit can still be written down.
 */
export function onMobilePersistNow(listener: () => void): () => void {
  const fromShell = (): void => listener();
  // Android does not always deliver a lifecycle callback before the process is
  // reclaimed, so `visibilitychange` is the second, earlier signal. Both are
  // registered, and both are removed — an unbalanced listener here would keep
  // firing against a stale session after the document is closed.
  const fromVisibility = (): void => {
    if (document.visibilityState === 'hidden') listener();
  };
  window.addEventListener('moduPersistNow', fromShell);
  document.addEventListener('visibilitychange', fromVisibility);
  return () => {
    window.removeEventListener('moduPersistNow', fromShell);
    document.removeEventListener('visibilitychange', fromVisibility);
  };
}
